import { renderYoutubeWidgetWithScript } from './modules/youtube-subscribe.js';
import { observeLazySources } from './modules/lazy-sources-loader.js';
import { getArticle } from './services/api';
import AdSlot from './modules/monetization/ad-slot';
import { monetizationManager } from './monetization.js';
import ArticleInfiniteLoader from './services/article/article-infinite-loader';
import Article from './services/article/article';
import ArticleChangeObserver from './services/article/article-change-observer';
import { getNestedText } from './services/article/get-nested-text.js';
import { hasDiageoViolatedWords } from './modules/monetization/has-diageo-violated-words';
import { getPolicyViolatedWords } from './modules/monetization/get-policy-violated-words';

import { addEventsOnShareButtons } from './modules/sharing';
import { enableTableOfContentsButton } from './modules/table-of-contents-button';
import { initEmbeds } from './modules/embeds';
import { initGallery } from './modules/gallery.js';
import { initComments } from './modules/comments';
import { isAditudeEnabled, setAditudeTargeting } from './modules/aditude';
import { initNewsBlocksAnalytics, initReadAlsoAnalytics } from './modules/analytics.js';
import { initRelativeContentSlot } from './modules/monetization/adsense-seacrh-ads.js';

const registerAdSlots = (article) => {
  if (window.adSharedConfigs) {
    const uniqueConfigs = Object.values(window.adSharedConfigs)
      .map((sharedConfig) => sharedConfig.getUniqueConfig())
      .filter((uniqueConfig) => article.adSlotIds.includes(uniqueConfig.gptConfig.code));

    const slots = uniqueConfigs.map((config) => new AdSlot(config, article.$root));

    const { id: articleID, author, tag, categorySlugs, subcategorySlugs, isEvergreen } = article;

    const contentText = getNestedText(article.$root);

    const violated_words = [
      ...new Set([...window.Site.targeting.violated_words, ...getPolicyViolatedWords(contentText)]),
    ];

    const targeting = Object.assign(window.Site.targeting, {
      articleID,
      author,
      tag,
      category: [...categorySlugs, ...subcategorySlugs],
      violated_words,
      isEvergreen,
      ...(!window.Site.targeting.has_diageo_words && {
        has_diageo_words: hasDiageoViolatedWords(contentText),
      }),
    });

    if (isAditudeEnabled) {
      setAditudeTargeting(targeting, true);
    } else {
      monetizationManager.targeting = targeting;
      monetizationManager.addSlots(slots);
    }
  }
};

const createArticle = (data, trendingMarkup) => {
  const article = new Article(data);
  article.render(trendingMarkup);
  observeLazySources(article.$root);
  enableTableOfContentsButton(article.$root);
  addEventsOnShareButtons(article.$root);
  initNewsBlocksAnalytics(article.$root);
  initReadAlsoAnalytics(article.$root);

  return article;
};

/* need for old browsers without defer */
document.addEventListener(
  `DOMContentLoaded`,
  () => {
    if (window.Site.youTubeChannelId) {
      renderYoutubeWidgetWithScript(
        document,
        { channelid: window.Site.youTubeChannelId },
        `c-article__ytsubscribe--show`,
      );
    }

    const $infiniteContainer = document.querySelector(`.js-infinite-container`);
    const articleInfiniteLoader = new ArticleInfiniteLoader($infiniteContainer);

    enableTableOfContentsButton();
    addEventsOnShareButtons();
    initNewsBlocksAnalytics();

    getArticle(window.Site.entity_id)
      .then(({ data: initialArticleData }) => {
        const articles = {};

        const initialArticle = new Article(
          initialArticleData,
          document.querySelector(`.js-article`),
        );

        initGallery(initialArticle.$root);
        initComments(initialArticle);
        initRelativeContentSlot(
          initialArticle.$body.querySelectorAll(`.js-relative-content-ad-block`),
        );
        initReadAlsoAnalytics(initialArticle.$root);

        if (initialArticle.hasReadAlso && FEATURES.INFINITE_SCROLL) {
          articleInfiniteLoader.addArticles(initialArticle.readAlso, 0);
        }

        articles[initialArticle.id] = initialArticle;
        initialArticle.viewed = true;

        if ($infiniteContainer) {
          const articleChangeObserver = new ArticleChangeObserver((currentArticleId) => {
            const article = articles[currentArticleId];

            if (article.url.pathname !== window.location.pathname) {
              window.history?.replaceState({}, article.title, article.url.pathname);
              window.document.title = articles[currentArticleId].title;
            }

            if (!article.viewed) {
              article.viewed = true;
              article.sendAnalytics();
            }
          });
          articleChangeObserver.observe(initialArticle.$body);

          articleInfiniteLoader.onLoad = (
            [{ data: articleData }, { data: trendingData }],
            pageNumber,
          ) => {
            const article = createArticle(
              articleData,
              trendingData.reduce((markup, trend) => markup + trend.attributes.content, ``),
            );

            articles[article.id] = article;
            articleChangeObserver.observe(article.$body);

            $infiniteContainer.appendChild(article.$root);
            initGallery(article.$root);

            registerAdSlots(article);

            initEmbeds(article.$root);
            initComments(article);

            if (FEATURES.INFINITE_SCROLL_GA4_TRACKING) {
              window.gtag?.(`event`, `infinite_scroll`, {
                scroll_depth: pageNumber,
              });
            }

            initRelativeContentSlot(
              article.$body.querySelectorAll(`.js-relative-content-ad-block`),
            );
          };
        }
      })
      .catch(console.error);

    /* initialization of lazy loading */
    observeLazySources();
  },
  false,
);
